import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Car, Menu, X, Phone, User, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import features from "@/config/features";

// Nav links filtered by version
const getAllNavLinks = () => {
  const links = [
    { name: "Acasă", path: "/", show: true },
    { name: "Servicii", path: "/servicii", show: true },
    { name: "Despre Noi", path: "/despre", show: features.aboutUs },
    { name: "Galerie", path: "/galerie", show: features.gallery },
    { name: "Programări", path: "/programari", show: features.bookings },
    { name: "Contact", path: "/contact", show: true },
    // { name: "Donează", path: "/doneaza", highlight: true, show: true },
  ];
  return links.filter(link => link.show);
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const navLinks = getAllNavLinks();

  return (
    <>
      {/* Opening Soon Banner - Only in Complete version */}
      {features.promoBanner && (
        <div className="bg-yellow-400 text-slate-900 py-2 px-4 text-center font-semibold text-sm" data-testid="opening-banner">
          <span className="inline-flex items-center gap-2">
            <span className="badge-pulse">🔥</span>
            DESCHIDERE ÎN CURÂND – Rezervă-ți locul acum!
            <span className="badge-pulse">🔥</span>
          </span>
        </div>
      )}

      <nav className="sticky top-0 z-50 glass border-b border-slate-200/50" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <div className="relative">
                <div className="h-10 md:h-12 flex items-center">
                  <img
                    src="/logo.png"
                    alt="Auto Cora"
                    className="h-12 w-auto bg-white p-1 rounded-md shadow-sm" 
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full" />
              </div>
              <div className="flex flex-col">
                <span className="font-extrabold text-xl md:text-2xl text-slate-900 tracking-tight font-['Archivo']">
                  AUTO CORA
                </span>
                <span className="text-[10px] md:text-xs text-slate-500 tracking-widest uppercase">
                  Școala de Șoferi
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors rounded-lg ${link.highlight
                      ? "text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      : location.pathname === link.path
                        ? "text-blue-600"
                        : "text-slate-700 hover:text-blue-600 hover:bg-blue-50"
                    }`}
                >
                  {link.highlight && <span className="mr-1">❤️</span>}
                  {link.name}
                  {location.pathname === link.path && !link.highlight && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full"
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <a href="tel:+40740245376" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">0740 245 376</span>
              </a>
              {user ? (
                <Link to="/dashboard">
                  <Button
                    data-testid="nav-dashboard-button"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 shadow-lg shadow-blue-600/20 btn-shine"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Contul Meu
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button
                    data-testid="nav-login-button"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 shadow-lg shadow-blue-600/20 btn-shine"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Autentificare
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-slate-200"
              data-testid="mobile-menu"
            >
              <div className="px-4 py-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === link.path
                        ? "bg-blue-50 text-blue-600"
                        : "text-slate-700 hover:bg-slate-50"
                      }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-4 border-t border-slate-200 mt-4">
                  {user ? (
                    <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                      <Button
                        data-testid="mobile-dashboard-button"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/20"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Contul Meu
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button
                        data-testid="mobile-login-button"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/20"
                      >
                        <LogIn className="w-4 h-4 mr-2" />
                        Autentificare
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
}
